// components/video/DisplacementOverlay.tsx
import React from 'react';
import { DisplacementResult, VideoDisplacement } from 'utils/geoCalculations';

interface DisplacementOverlayProps {
  mousePosition: { x: number; y: number } | null;
  videoDisplacement: VideoDisplacement | null;
  displacementResult: DisplacementResult | null;
  videoRect: DOMRect | null;
  cameraBearing?: number | null; // Bearing da câmera da foto
  show: boolean;
}

const DisplacementOverlay: React.FC<DisplacementOverlayProps> = ({
  mousePosition,
  videoDisplacement,
  displacementResult,
  videoRect,
  cameraBearing,
  show
}) => {
  console.log('🎨 DisplacementOverlay render:', {
    show,
    hasMousePosition: !!mousePosition,
    hasVideoRect: !!videoRect,
    hasDisplacement: !!videoDisplacement,
    hasResult: !!displacementResult,
    cameraBearing,
    mousePosition,
    videoRect: videoRect
      ? { width: videoRect.width, height: videoRect.height }
      : null
  });

  if (!show) {
    console.log('❌ DisplacementOverlay: show is false');
    return null;
  }

  if (!mousePosition) {
    console.log('❌ DisplacementOverlay: no mousePosition');
    return null;
  }

  if (!videoRect) {
    console.log('❌ DisplacementOverlay: no videoRect');
    return null;
  }

  if (!videoDisplacement) {
    console.log('❌ DisplacementOverlay: no videoDisplacement');
    return null;
  }

  if (!displacementResult) {
    console.log('❌ DisplacementOverlay: no displacementResult');
    return null;
  }

  console.log('✅ DisplacementOverlay: RENDERING!');

  const baseX = videoRect.width / 2;
  const baseY = videoRect.height;

  const getLineColor = () => {
    if (Math.abs(videoDisplacement.x) > 0.2) {
      return videoDisplacement.x > 0 ? '#28a745' : '#007bff';
    }
    return '#ffc107';
  };

  const lineColor = getLineColor();

  // Calcular direção relativa baseada no bearing
  const getDirectionLabel = (bearing: number): string => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(bearing / 45) % 8;
    return directions[index];
  };

  console.log('🎨 Drawing:', {
    from: { x: baseX, y: baseY },
    to: mousePosition,
    color: lineColor,
    bearing: cameraBearing
  });

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10
      }}
    >
      {/* Linha de deslocamento */}
      <line
        x1={baseX}
        y1={baseY}
        x2={mousePosition.x}
        y2={mousePosition.y}
        stroke={lineColor}
        strokeWidth="3"
        strokeDasharray="5,5"
        opacity="0.8"
      />

      {/* Círculo na base (GPS original) */}
      <circle
        cx={baseX}
        cy={baseY}
        r="8"
        fill="#dc3545"
        opacity="0.8"
        stroke="white"
        strokeWidth="2"
      />

      {/* Círculo no mouse (posição ajustada) */}
      <circle
        cx={mousePosition.x}
        cy={mousePosition.y}
        r="10"
        fill={lineColor}
        opacity="0.9"
        stroke="white"
        strokeWidth="2"
      />

      {/* Seta indicando direção da câmera (se bearing disponível) */}
      {cameraBearing !== null && cameraBearing !== undefined && (
        <>
          {/* Linha de direção da câmera */}
          <line
            x1={baseX}
            y1={baseY}
            x2={baseX}
            y2={baseY - 60}
            stroke="#007bff"
            strokeWidth="2"
            opacity="0.6"
            transform={`rotate(${cameraBearing}, ${baseX}, ${baseY})`}
          />

          {/* Seta na ponta da linha de direção */}
          <polygon
            points={`${baseX},${baseY - 65} ${baseX - 5},${baseY - 55} ${
              baseX + 5
            },${baseY - 55}`}
            fill="#007bff"
            opacity="0.8"
            stroke="white"
            strokeWidth="1"
            transform={`rotate(${cameraBearing}, ${baseX}, ${baseY})`}
          />
        </>
      )}

      {/* Info Box */}
      <g
        transform={`translate(${mousePosition.x + 15}, ${
          mousePosition.y - 10
        })`}
      >
        <rect
          x="0"
          y="0"
          width="180"
          height={
            cameraBearing !== null && cameraBearing !== undefined ? 120 : 100
          }
          fill="rgba(0,0,0,0.8)"
          rx="4"
          stroke="white"
          strokeWidth="1"
        />

        {/* Título */}
        <text
          x="8"
          y="16"
          fill="#ffc107"
          fontSize="12"
          fontWeight="bold"
          fontFamily="monospace"
        >
          GPS Ajustado
        </text>

        {/* Horizontal */}
        <text x="8" y="32" fill="white" fontSize="11" fontFamily="monospace">
          Horizontal: {displacementResult.horizontalOffset.toFixed(2)}m
        </text>

        {/* Profundidade */}
        <text x="8" y="48" fill="white" fontSize="11" fontFamily="monospace">
          Profund.: {displacementResult.depthOffset.toFixed(2)}m
        </text>

        {/* Bearing da câmera (se disponível) */}
        {cameraBearing !== null && cameraBearing !== undefined && (
          <>
            <text
              x="8"
              y="64"
              fill="#007bff"
              fontSize="11"
              fontFamily="monospace"
            >
              Câmera: {cameraBearing.toFixed(1)}° (
              {getDirectionLabel(cameraBearing)})
            </text>

            {/* Bearing do deslocamento */}
            <text
              x="8"
              y="80"
              fill="#ffc107"
              fontSize="11"
              fontFamily="monospace"
            >
              Placa: {displacementResult.bearing.toFixed(1)}°
            </text>

            {/* GPS ajustado */}
            <text
              x="8"
              y="96"
              fill="#28a745"
              fontSize="9"
              fontFamily="monospace"
            >
              {displacementResult.adjustedGPS.lat.toFixed(6)}
            </text>
            <text
              x="8"
              y="107"
              fill="#28a745"
              fontSize="9"
              fontFamily="monospace"
            >
              {displacementResult.adjustedGPS.lng.toFixed(6)}
            </text>
          </>
        )}

        {/* Se bearing não disponível */}
        {(cameraBearing === null || cameraBearing === undefined) && (
          <>
            <text
              x="8"
              y="64"
              fill="#ffc107"
              fontSize="11"
              fontFamily="monospace"
            >
              Bearing: {displacementResult.bearing.toFixed(1)}°
            </text>

            <text
              x="8"
              y="76"
              fill="#28a745"
              fontSize="9"
              fontFamily="monospace"
            >
              {displacementResult.adjustedGPS.lat.toFixed(6)}
            </text>
            <text
              x="8"
              y="87"
              fill="#28a745"
              fontSize="9"
              fontFamily="monospace"
            >
              {displacementResult.adjustedGPS.lng.toFixed(6)}
            </text>
          </>
        )}
      </g>
    </svg>
  );
};

export default DisplacementOverlay;
